import { filterProducts } from "./catalog";
import { buildSearchRecovery, type SearchRecovery } from "./search-recovery";
import { productSearchKey } from "./product-search";
import { parseProductSearchQuery } from "./search-query";
import { qualifiedBrandQuery, productMatchesSearchBrand } from "./search-brands";
import { t } from "./i18n";
import type { ProductFilters, ProductView } from "./types";

export const MULTI_SEARCH_PREVIEW = 2;
export const MULTI_SEARCH_PAGE = 12;
export const BRANDED_SEARCH_PREVIEW = 4;
export interface MultiSearchGroup {
  query: string;
  primary: ProductView[];
  recovery: SearchRecovery;
  branded?: boolean;
  alternatives?: { query: string; products: ProductView[] };
}

export function multiSearchPreview(group: MultiSearchGroup, scope: string): number {
  return group.branded && scope !== "alternatives" ? BRANDED_SEARCH_PREVIEW : MULTI_SEARCH_PREVIEW;
}

export function buildMultiSearchGroups(products: ProductView[], filters: ProductFilters, recovery = true): MultiSearchGroup[] {
  const parsed = parseProductSearchQuery(filters.query, products);
  if (parsed.error || !parsed.items.length || (!parsed.multiple && !qualifiedBrandQuery(products, parsed.items[0]!))) return [];
  return parsed.items.map(query => {
    const scoped = { ...filters, query };
    const primary = filterProducts(products, scoped);
    const found = recovery ? buildSearchRecovery(products, scoped, primary) : { groups: [], totalMatches: primary.length };
    const branded = qualifiedBrandQuery(products, query);
    // Alternatives are NOT matches for the original query. Keep exact matches
    // (including outside-filter recovery) first, and never inflate their count.
    const alternatives = branded && found.totalMatches <= BRANDED_SEARCH_PREVIEW
      ? { query: branded.productQuery, products: filterProducts(products, { ...scoped, query: branded.productQuery })
          .filter(product => !productMatchesSearchBrand(product, branded.brandKey)) }
      : undefined;
    return { query, primary, recovery: found, branded: Boolean(branded), alternatives };
  });
}

export function multiSearchDistinctProducts(groups: readonly MultiSearchGroup[]): ProductView[] {
  const unique = new Map<string, ProductView>();
  for (const group of groups) for (const product of [...group.primary, ...group.recovery.groups.flatMap(item => item.products)]) {
    unique.set(productSearchKey(product), product);
  }
  return [...unique.values()];
}

function escape(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

interface MultiSearchView {
  groups: readonly MultiSearchGroup[];
  limits: ReadonlyMap<string, number>;
  renderProduct: (product: ProductView) => string;
  recoveryLabel: (label: MultiSearchGroup["recovery"]["groups"][number]["label"]) => string;
  count: (value: number) => string;
  canWatch: boolean;
}

/** Cards use the same quantity/dialog handlers as the ordinary feed. Query text
 * remains escaped plain data; expansion never rewrites global filters/input. */
export function multiSearchMarkup(view: MultiSearchView): string {
  function cards(rows: readonly ProductView[], index: number, scope: string): string {
    const key = `${index}:${scope}`;
    const preview = multiSearchPreview(view.groups[index]!, scope);
    const limit = view.limits.get(key) ?? preview;
    const shown = rows.slice(0, limit);
    const remaining = rows.length - shown.length;
    return `<div class="search-recovery-products multi-search-products">${shown.map(view.renderProduct).join("")}</div>
      <div class="multi-search-actions">
        ${remaining > 0 ? `<button type="button" class="search-recovery-action" data-multi-search-more="${key}" aria-controls="multi-search-${index}">${escape(t("multiSearchMore", { count: view.count(remaining) }))}</button>` : ""}
        ${limit > preview ? `<button type="button" class="multi-search-collapse" data-multi-search-collapse="${key}" aria-controls="multi-search-${index}">${escape(t("multiSearchCollapse"))}</button>` : ""}
      </div>`;
  }
  return view.groups.map((group, index) => `<section class="multi-search-section" id="multi-search-${index}" aria-labelledby="multi-search-title-${index}">
    <div class="multi-search-heading"><h3 id="multi-search-title-${index}">${escape(group.query)}</h3><span>${view.count(group.recovery.totalMatches)}</span></div>
    ${group.primary.length ? `${group.recovery.groups.length ? `<p class="multi-search-scope">${escape(t("searchRecoverySelected"))}</p>` : ""}${cards(group.primary, index, "primary")}` : `<p class="multi-search-empty">${escape(t(group.recovery.groups.length ? "multiSearchEmptyScope" : "multiSearchEmpty"))}</p>`}
    ${group.recovery.groups.map(recovery => `<div class="multi-search-recovery"><h4>${escape(view.recoveryLabel(recovery.label))}</h4>${cards(recovery.products, index, recovery.scope)}</div>`).join("")}
    ${!group.recovery.totalMatches && view.canWatch ? `<button type="button" class="status-watch-button" data-watch-query="${escape(group.query)}">${escape(t("notifyWhenAppears", { query: group.query }))}</button>` : ""}
    ${group.alternatives?.products.length ? `<div class="multi-search-recovery multi-search-alternatives"><h4>${escape(t("searchOtherBrands", { query: group.alternatives.query }))}</h4>${cards(group.alternatives.products, index, "alternatives")}</div>` : ""}
  </section>`).join("");
}
